import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../lib/constants';

interface FamilyMember {
  id: number;
  username: string;
  email: string;
  first_name: string;
  role: string;
  status: string;
  joined_at: string;
}

interface PendingMember {
  id: number;
  username: string;
  email: string;
  first_name: string;
  joined_at: string;
}

interface FamilyData {
  family: {
    id: number;
    name: string;
    join_code: string;
    created_at: string;
  };
  membership: {
    role: string;
    status: string;
    joined_at: string;
  };
  members: FamilyMember[];
  pending_members: PendingMember[];
}

interface CurrentUser {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
}

export default function FamilyInfoSection() {
  const [familyData, setFamilyData] = useState<FamilyData | null>(null);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);

  const fetchFamilyInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch both family data and current user in parallel
      const [familyResponse, userResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/family/`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/api/current-user/`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setFamilyData(familyResponse.data);
      setCurrentUser(userResponse.data);
      setError(null);
    } catch (err: any) {
      if (err.response?.status === 401) {
        window.location.href = '/login';
        return;
      }
      setError('Failed to load family information');
      console.error('Error fetching family info:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveMember = async (userId: number) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/api/family/approve/${userId}/`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Refresh family data
      fetchFamilyInfo();
    } catch (err: any) {
      console.error('Error approving member:', err);
      alert('Failed to approve member');
    }
  };

  const handleDenyMember = async (userId: number) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/api/family/deny/${userId}/`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Refresh family data
      fetchFamilyInfo();
    } catch (err: any) {
      console.error('Error denying member:', err);
      alert('Failed to deny member');
    }
  };

  const handleSendInvite = async () => {
    if (!inviteEmail.trim()) {
      alert('Please enter an email address');
      return;
    }

    setInviteLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/api/family/invite/`, {
        email: inviteEmail.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Invitation sent successfully!');
      setShowInviteModal(false);
      setInviteEmail('');
    } catch (err: any) {
      console.error('Error sending invite:', err);
      const errorMessage = err.response?.data?.error || 'Failed to send invitation';
      alert(errorMessage);
    } finally {
      setInviteLoading(false);
    }
  };

  useEffect(() => {
    fetchFamilyInfo();
  }, []);

  if (loading) {
    return <div style={{ padding: 20, textAlign: 'center' }}>Loading family information...</div>;
  }

  if (error) {
    return <div style={{ padding: 20, color: 'red' }}>{error}</div>;
  }

  if (!familyData?.family) {
    return (
      <div style={{ padding: 20, backgroundColor: '#f8f9fa', borderRadius: 8, marginBottom: 24 }}>
        <h3 style={{ margin: 0, marginBottom: 16 }}>No Family</h3>
        <p>You are not currently part of a family. Create a family or join one using a join code.</p>
      </div>
    );
  }

  const { family, membership, members, pending_members } = familyData;
  const isAdmin = membership.role === 'admin' || membership.role === 'owner';
  const isPending = membership.status === 'pending';

  return (
    <div style={{ backgroundColor: '#f8f9fa', borderRadius: 8, padding: 24, marginBottom: 32 }}>
      <h3 style={{ margin: 0, marginBottom: 20, color: '#2563eb', fontSize: 20, fontWeight: 600 }}>
        Family Information
      </h3>
      
      {isPending ? (
        /* Pending Membership Display */
        <div>
          <h4 style={{ margin: 0, marginBottom: 8, fontSize: 18, fontWeight: 500 }}>
            {family.name}
          </h4>
          <div style={{ 
            backgroundColor: '#fff3cd', 
            color: '#856404', 
            padding: 16, 
            borderRadius: 6,
            border: '1px solid #ffeaa7'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 16, fontWeight: 500 }}>⏳ Pending Approval</span>
            </div>
            <p style={{ margin: 0, fontSize: 14 }}>
              Your request to join this family is pending approval from an admin. 
              You'll gain access to family features once approved.
            </p>
          </div>
        </div>
      ) : (
        /* Approved Membership Display */
        <>
          <div style={{ marginBottom: 20 }}>
            <h4 style={{ margin: 0, marginBottom: 8, fontSize: 18, fontWeight: 500 }}>
              {family.name}
            </h4>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <span style={{ fontSize: 14, color: '#666' }}>Join Code:</span>
              <span style={{ 
                backgroundColor: '#e3f2fd', 
                color: '#1976d2', 
                padding: '4px 12px', 
                borderRadius: 4, 
                fontFamily: 'monospace',
                fontSize: 16,
                fontWeight: 600
              }}>
                {family.join_code}
              </span>
              <button
                onClick={() => {
                  const url = `${window.location.origin}/sign-up?join_code=${family.join_code}`;
                  navigator.clipboard.writeText(url);
                  alert('Sign-up link copied to clipboard!');
                }}
                style={{
                  backgroundColor: '#2563eb',
                  color: 'white',
                  border: 'none',
                  padding: '4px 8px',
                  borderRadius: 4,
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Copy Link
              </button>
              {isAdmin && (
                <button
                  onClick={() => setShowInviteModal(true)}
                  style={{
                    backgroundColor: '#16a34a',
                    color: 'white',
                    border: 'none',
                    padding: '4px 8px',
                    borderRadius: 4,
                    fontSize: 12,
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                >
                  Send Invite
                </button>
              )}
            </div>
            <p style={{ margin: 0, fontSize: 14, color: '#666' }}>
              Share this code with others to invite them to your family.
            </p>
          </div>

          {/* Current Members */}
          <div style={{ marginBottom: 20 }}>
            <h5 style={{ margin: 0, marginBottom: 12, fontSize: 16, fontWeight: 500 }}>
              Family Members ({members.length})
            </h5>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {members.map((member) => (
            <div
              key={member.id}
              style={{
                backgroundColor: 'white',
                padding: '8px 12px',
                borderRadius: 6,
                border: '1px solid #e0e0e0',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              <span style={{ fontSize: 14, fontWeight: 500 }}>
                {member.first_name || member.username}
              </span>
              {member.role === 'owner' && (
                <span style={{
                  backgroundColor: '#ff9800',
                  color: 'white',
                  padding: '2px 6px',
                  borderRadius: 3,
                  fontSize: 11,
                  fontWeight: 500
                }}>
                  Owner
                </span>
              )}
              {member.role === 'admin' && (
                <span style={{
                  backgroundColor: '#4caf50',
                  color: 'white',
                  padding: '2px 6px',
                  borderRadius: 3,
                  fontSize: 11,
                  fontWeight: 500
                }}>
                  Admin
                </span>
              )}
              {currentUser && member.id === currentUser.id && (
                <span style={{
                  backgroundColor: '#2196f3',
                  color: 'white',
                  padding: '2px 6px',
                  borderRadius: 3,
                  fontSize: 11,
                  fontWeight: 500
                }}>
                  You
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
        </>
      )}

      {/* Pending Members (Admin Only) - only show for approved admins */}
      {!isPending && isAdmin && pending_members && pending_members.length > 0 && (
        <div>
          <h5 style={{ margin: 0, marginBottom: 12, fontSize: 16, fontWeight: 500, color: '#f57c00' }}>
            Pending Approvals ({pending_members.length})
          </h5>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {pending_members.map((member) => (
              <div
                key={member.id}
                style={{
                  backgroundColor: 'white',
                  padding: 16,
                  borderRadius: 6,
                  border: '1px solid #ffcc02',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>
                    {member.first_name || member.username}
                  </div>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    {member.email}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => handleApproveMember(member.id)}
                    style={{
                      backgroundColor: '#4caf50',
                      color: 'white',
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: 4,
                      fontSize: 12,
                      fontWeight: 500,
                      cursor: 'pointer'
                    }}
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => handleDenyMember(member.id)}
                    style={{
                      backgroundColor: '#f44336',
                      color: 'white',
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: 4,
                      fontSize: 12,
                      fontWeight: 500,
                      cursor: 'pointer'
                    }}
                  >
                    Deny
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invite Modal - only show for approved members */}
      {!isPending && showInviteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: 24,
            borderRadius: 8,
            maxWidth: 400,
            width: '90%',
            maxHeight: '90%',
            overflow: 'auto'
          }}>
            <h3 style={{ margin: 0, marginBottom: 16, fontSize: 18, fontWeight: 600 }}>
              Invite to Family
            </h3>
            <p style={{ margin: 0, marginBottom: 16, fontSize: 14, color: '#666' }}>
              Send an email invitation to join your family. They'll receive a link that automatically includes your family join code.
            </p>
            <input
              type="email"
              placeholder="Enter email address"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              style={{
                width: '100%',
                padding: 10,
                borderRadius: 6,
                border: '1px solid #ddd',
                marginBottom: 16,
                fontSize: 14
              }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowInviteModal(false);
                  setInviteEmail('');
                }}
                style={{
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: 4,
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSendInvite}
                disabled={inviteLoading}
                style={{
                  backgroundColor: '#16a34a',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: 4,
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: inviteLoading ? 'not-allowed' : 'pointer',
                  opacity: inviteLoading ? 0.6 : 1
                }}
              >
                {inviteLoading ? 'Sending...' : 'Send Invite'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}